import { ReactNode, useCallback, useState } from 'react';
import { useProductName } from '../lib/use-brand';
import { useTranslation } from '../lib/i18n';
import Link from 'next/link';
import { chatHandoffLink } from '../lib/chat-handoff';
import { classifyDashboardErrors } from '../lib/dashboard-error-kind';

// The error tint differs per host surface (Orion's PageSurface renders on a darker app chrome, Symphony's
// DashboardFrame on a light one), so the caller passes the three colours; everything else — the
// aggregation, the bundled copy / hand-off, and the (non-overlapping) layout — is shared.
export interface ErrorBannerPalette {
  color: string;
  background: string;
  border: string;
}

/**
 * The dashboard's self-heal error stripe. It's a FLOW element (not absolute) so it pushes the dashboard
 * down instead of overlapping it. Orion is a consumer product, not a dev console, so the raw error text is
 * DELIBERATELY not shown — the stripe reads as a calm one-line notice. The host's full, deduped error list
 * (a failed widget `sym.call` or an uncaught runtime error) rides only in the Copy payload and the "Ask
 * Orion to fix" hand-off, so a single tap reports them all together. The host owns the list (dedup +
 * ordering); this only summarises + bundles it.
 *
 * WHAT IT MUST NOT DO IS BLAME US FOR SOMEONE ELSE'S OUTAGE. When every collected error is a connected
 * service failing to answer (`classifyDashboardErrors` → `outage`), the page is fine and there is nothing
 * for the assistant to fix: the stripe names the service, reads as an outage, and offers only a retry — no
 * "didn't load correctly", no Copy, no hand-off. Only genuine defects get the old treatment, and only the
 * defect lines travel in the payload, so the outage noise never reaches the assistant either.
 */
export function DashboardErrorBanner({
  basePath,
  title,
  errors,
  palette,
  onRetry,
}: {
  basePath: string;
  title: string;
  errors: string[];
  palette: ErrorBannerPalette;
  // Re-fetch this dashboard. Falls back to a full reload when the host has nothing finer to offer.
  onRetry?: () => void;
}) {
  const { kind, defects, services } = classifyDashboardErrors(errors);
  return kind === 'outage' ? (
    <OutageNotice services={services} palette={palette} onRetry={onRetry} />
  ) : (
    <DefectNotice basePath={basePath} title={title} errors={defects} palette={palette} />
  );
}

// Nothing on our side failed: a connected service is not answering. The cards already show their own
// designed "couldn't reach X" state, so this only names who is down and offers the one action that can
// help — asking again.
function OutageNotice({ services, palette, onRetry }: { services: string[]; palette: ErrorBannerPalette; onRetry?: () => void }) {
  const { t } = useTranslation();
  const heading = services.length ? t('dashboardError.outage.heading', { service: services.join(', ') }) : t('dashboardError.outage.headingGeneric');
  const retry = useCallback(() => {
    if (onRetry) onRetry();
    else if (typeof window !== 'undefined') window.location.reload();
  }, [onRetry]);
  return (
    <Stripe palette={palette}>
      <span style={{ flex: 1, minWidth: 0 }}>{heading}</span>
      <StripeAction onClick={retry} label={t('common.retry')} />
    </Stripe>
  );
}

function DefectNotice({ basePath, title, errors, palette }: { basePath: string; title: string; errors: string[]; palette: ErrorBannerPalette }) {
  const count = errors.length;
  const productName = useProductName();
  const { t } = useTranslation();
  // What the USER sees — a friendly, non-technical summary. No count, no error strings. (The real errors are
  // in the Copy payload / hand-off below.)
  const heading = count === 1 ? t('dashboardError.heading.one') : t('dashboardError.heading.other');
  const bundle = count === 1 ? errors[0] : errors.map((e) => `- ${e}`).join('\n');
  // One paste-ready block bundling every collected error — for the copy button and the chat hand-off.
  const errorText =
    count === 1 ? t('dashboardError.copyText.one', { title, bundle }) : t('dashboardError.copyText.other', { title, errorCount: count, bundle });
  const handoffMessage =
    count === 1 ? t('dashboardError.handoff.one', { title, bundle }) : t('dashboardError.handoff.other', { title, errorCount: count, bundle });
  const link = chatHandoffLink({ kind: 'new', message: handoffMessage }, basePath);

  const [copied, setCopied] = useState(false);
  const copyError = useCallback(() => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };
    const write = navigator.clipboard?.writeText(errorText);
    if (write) {
      void write.then(done, () => undefined);
    } else {
      done();
    }
  }, [errorText]);

  return (
    <Stripe palette={palette}>
      {/* Only the friendly summary is shown. The raw errors are intentionally kept off-screen — they travel
          in the Copy payload and the "Ask Orion to fix" hand-off, not in front of the user. */}
      <span style={{ flex: 1, minWidth: 0 }}>{heading}</span>
      <StripeAction onClick={copyError} label={copied ? t('dashboardError.copied') : t('dashboardError.copy')} />
      <Link
        href={link.route}
        as={link.path}
        prefetch={false}
        style={{ color: 'rgba(var(--ink-rgb),0.96)', textDecoration: 'underline', whiteSpace: 'nowrap' }}
      >
        {t('dashboardError.askToFix', { productName })}
      </Link>
    </Stripe>
  );
}

function Stripe({ palette, children }: { palette: ErrorBannerPalette; children: ReactNode }) {
  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
        padding: '10px 14px',
        fontSize: 13,
        lineHeight: 1.5,
        color: palette.color,
        background: palette.background,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
      }}
    >
      {children}
    </div>
  );
}

// The stripe's inline text action. One implementation for both notices, so the stripe keeps a single
// hand-rolled control (see test/button-spec.test.ts's pending-migration ratchet).
function StripeAction({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 13,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
